import React, {useEffect, useState} from "react";
import type {FormEvent} from "react";
import {
    confirmOwnSecondFactor,
    disableOwnSecondFactor,
    formatApiError,
    getOwnSecondFactor,
    OWN_SECOND_FACTOR_ENROLLMENT_MINUTES,
    SECOND_FACTOR_DEFAULT_SESSION_DAYS,
    startOwnSecondFactor,
} from "../api";
import type {OwnSecondFactorEnrollment, OwnSecondFactorStatus} from "../api";
import {apiErrorCode, messageForCode} from "../api/error-codes";
import {useAuth} from "../auth";
import {formatDate, groupSecondFactorSecret} from "../utils";
import {CopyButton} from "./CopyButton";
import {ErrorBanner} from "./ErrorBanner";

const CODE_LENGTH = 6;

const OWN_SECOND_FACTOR_ERRORS = {
    second_factor_invalid_code:
        "Код не подошёл. Введите новые шесть цифр из приложения; если не помогает — проверьте, что время на телефоне выставляется автоматически.",
    second_factor_enrollment_expired: `Прошло больше ${OWN_SECOND_FACTOR_ENROLLMENT_MINUTES} минут, этот QR-код больше не действует. Нажмите «Включить» ещё раз.`,
    second_factor_too_many_attempts: "Слишком много неверных кодов. Подождите до 15 минут и попробуйте снова.",
    second_factor_already_enabled: "2FA уже включена.",
    second_factor_not_enabled: "2FA уже выключена.",
} as const;

/** Шесть цифр из приложения: пробелы и прочее при вставке отбрасываются. */
function normalizeCode(raw: string): string {
    return raw.replace(/\D/g, "").slice(0, CODE_LENGTH);
}

interface CodeFieldProps {
    value: string;
    disabled: boolean;
    onChange: (code: string) => void;
}

function CodeField({value, disabled, onChange}: CodeFieldProps): JSX.Element {
    return (
        <div className="form-group own-second-factor-code">
            <label htmlFor="own-second-factor-code">Код из приложения</label>
            <input
                id="own-second-factor-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                placeholder="000000"
                value={value}
                onChange={(e) => onChange(normalizeCode(e.target.value))}
                disabled={disabled}
            />
        </div>
    );
}

/**
 * Раздел «Двухфакторный вход» в настройках кабинета: мерчант или трейдер сам
 * включает 2FA (QR → код из приложения) и выключает её текущим кодом. Срок
 * входа задаёт админ, здесь он только виден. QR и ключ живут только в этом
 * компоненте, пока открыт шаг включения.
 */
export function OwnSecondFactor(): JSX.Element {
    const {token} = useAuth();
    const [status, setStatus] = useState<OwnSecondFactorStatus | null>(null);
    const [loadError, setLoadError] = useState<unknown>(null);
    const [enrollment, setEnrollment] = useState<OwnSecondFactorEnrollment | null>(null);
    const [disabling, setDisabling] = useState(false);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const reload = async (): Promise<void> => {
        try {
            setStatus(await getOwnSecondFactor(token));
            setLoadError(null);
        } catch (err) {
            setLoadError(err);
        }
    };

    useEffect(() => {
        let cancelled = false;
        getOwnSecondFactor(token)
            .then((next) => {
                if (!cancelled) setStatus(next);
            })
            .catch((err: unknown) => {
                if (!cancelled) setLoadError(err);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const reset = (): void => {
        setEnrollment(null);
        setDisabling(false);
        setCode("");
        setError(null);
    };

    const refuse = (err: unknown): void => {
        const refusal = apiErrorCode(err);
        setError(messageForCode(err, OWN_SECOND_FACTOR_ERRORS) ?? formatApiError(err));
        setCode("");
        if (refusal === "second_factor_enrollment_expired") setEnrollment(null);
        if (refusal === "second_factor_already_enabled" || refusal === "second_factor_not_enabled") {
            setEnrollment(null);
            setDisabling(false);
            void reload();
        }
    };

    const run = async (action: () => Promise<void>): Promise<void> => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            await action();
        } catch (err) {
            refuse(err);
        } finally {
            setBusy(false);
        }
    };

    const start = (): Promise<void> =>
        run(async () => {
            setEnrollment(await startOwnSecondFactor(token));
            setCode("");
        });

    const confirm = (e: FormEvent): Promise<void> => {
        e.preventDefault();
        if (code.length !== CODE_LENGTH) return Promise.resolve();
        return run(async () => {
            setStatus(await confirmOwnSecondFactor(token, code));
            reset();
            setNotice("2FA включена. Остальные сессии закрыты; при следующем входе понадобится код из приложения.");
        });
    };

    const disable = (e: FormEvent): Promise<void> => {
        e.preventDefault();
        if (code.length !== CODE_LENGTH) return Promise.resolve();
        return run(async () => {
            setStatus(await disableOwnSecondFactor(token, code));
            reset();
            setNotice("2FA выключена. Дальше вход только по паролю.");
        });
    };

    const enabled = Boolean(status?.enabled);

    return (
        <section className="settings-card own-second-factor" aria-labelledby="own-second-factor-title">
            <div className="own-second-factor-head">
                <h2 id="own-second-factor-title">Двухфакторный вход</h2>
                {status && (
                    <span className="own-second-factor-state">
                        <span className={`status-dot ${enabled ? "dot-active" : "dot-inactive"}`}/>
                        {enabled ? "Включена" : "Выключена"}
                    </span>
                )}
            </div>

            {!status && !loadError && <p className="own-second-factor-text">Загрузка…</p>}
            <ErrorBanner error={loadError}/>
            {notice && <div className="alert alert-info" role="status">{notice}</div>}
            {error && <div className="alert alert-error" role="alert">{error}</div>}

            {status && !enabled && !enrollment && (
                <>
                    <p className="own-second-factor-text">
                        При входе кроме пароля понадобятся шесть цифр из приложения на телефоне — Google
                        Authenticator, Authy или другого. Если пароль узнают, без телефона в кабинет не войти.
                    </p>
                    {status.enrollmentExpiresAt && (
                        <p className="own-second-factor-hint">
                            Включение начато, но код не введён. QR-код показывается один раз — нажмите «Включить»,
                            чтобы получить новый.
                        </p>
                    )}
                    <div className="own-second-factor-actions">
                        <button className="btn btn-primary" type="button" onClick={() => void start()} disabled={busy}>
                            {busy ? "…" : "Включить"}
                        </button>
                    </div>
                </>
            )}

            {status && !enabled && enrollment && (
                <form className="own-second-factor-enroll" onSubmit={(e) => void confirm(e)}>
                    <ol className="own-second-factor-steps">
                        <li>
                            Отсканируйте QR-код приложением для 2FA.
                            <div className="second-factor-qr">
                                <img src={enrollment.qrCode} alt="QR-код для приложения 2FA"/>
                            </div>
                        </li>
                        <li>
                            Не получается отсканировать — добавьте ключ вручную:
                            <div className="own-second-factor-secret">
                                <span className="second-factor-secret mono">{groupSecondFactorSecret(enrollment.secret)}</span>
                                <CopyButton value={enrollment.secret} label="ключ"/>
                            </div>
                        </li>
                        <li>
                            Введите шесть цифр, которые показывает приложение.
                            <CodeField value={code} disabled={busy} onChange={setCode}/>
                        </li>
                    </ol>
                    <p className="own-second-factor-hint">
                        QR-код и ключ действуют {OWN_SECOND_FACTOR_ENROLLMENT_MINUTES} минут. Пока код не введён, вход
                        остаётся прежним.
                    </p>
                    <div className="own-second-factor-actions">
                        <button className="btn btn-primary" type="submit" disabled={busy || code.length !== CODE_LENGTH}>
                            {busy ? "…" : "Подтвердить"}
                        </button>
                        <button className="btn btn-ghost" type="button" onClick={reset} disabled={busy}>
                            Отмена
                        </button>
                    </div>
                </form>
            )}

            {status && enabled && (
                <>
                    <dl className="own-second-factor-facts">
                        <dt>Включена</dt>
                        <dd>{formatDate(status.issuedAt)}</dd>
                        <dt>Вход действует</dt>
                        <dd>
                            {status.sessionDays ?? SECOND_FACTOR_DEFAULT_SESSION_DAYS} сут., потом снова пароль и код.
                            Срок задаёт администратор.
                        </dd>
                    </dl>
                    {disabling ? (
                        <form className="own-second-factor-disable" onSubmit={(e) => void disable(e)}>
                            <p className="own-second-factor-text">Чтобы выключить, введите текущий код из приложения.</p>
                            <CodeField value={code} disabled={busy} onChange={setCode}/>
                            <div className="own-second-factor-actions">
                                <button className="btn btn-danger" type="submit" disabled={busy || code.length !== CODE_LENGTH}>
                                    {busy ? "…" : "Выключить"}
                                </button>
                                <button className="btn btn-ghost" type="button" onClick={reset} disabled={busy}>
                                    Отмена
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="own-second-factor-actions">
                            <button
                                className="btn btn-ghost"
                                type="button"
                                onClick={() => {
                                    setNotice(null);
                                    setDisabling(true);
                                }}
                            >
                                Выключить
                            </button>
                        </div>
                    )}
                </>
            )}

            <p className="own-second-factor-lost">
                Потеряли телефон — напишите администратору: он выключит 2FA, и вы войдёте по паролю.
            </p>
        </section>
    );
}
